/**
 * Cloudflare Turnstile, loaded only when a site key is configured.
 *
 * The key is public — it is compiled into the bundle and visible in page
 * source — so it is a build-time variable rather than a secret. With no key
 * the widget never loads and no request is made to Cloudflare, which is what
 * keeps local development and tests offline.
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile';

export interface TurnstileApi {
  render(
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
      theme?: 'light' | 'dark' | 'auto';
      language?: string;
    },
  ): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const siteKey: string = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

export const turnstileEnabled = siteKey.length > 0;

let loader: Promise<TurnstileApi> | null = null;

/**
 * Loads the script once, however many callers ask. Rejects rather than
 * hanging if the script cannot be fetched, so the form can decide what to do
 * about it.
 */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (!turnstileEnabled) {
    return Promise.reject(new Error('Turnstile is not configured'));
  }
  if (loader) return loader;

  const attempt = new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error('Turnstile loaded without exposing its api'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener(
      'error',
      () => {
        // A script element that failed never refetches, so it must go for the
        // next attempt to insert a fresh one.
        script.remove();
        reject(new Error('Turnstile failed to load'));
      },
      { once: true },
    );

    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });

  loader = attempt;
  // A failure stays cached otherwise, and one flaky fetch would disable the
  // widget until the page is reloaded.
  attempt.catch(() => {
    if (loader === attempt) loader = null;
  });

  return attempt;
}
